import { z } from "zod";

import {
  assertDocumentCustomTextBoilerplateEditorSettings,
  DocumentCustomTextBoilerplateSchema,
  DocumentCustomTextModalSlotsSchema,
  emptyDocumentCustomTextBoilerplate,
  emptyDocumentCustomTextModalSlots,
} from "~/shared/documents/schemas/document-custom-text.schema";
import { DisplayUomOverrideSchema } from "~/shared/documents/schemas/document-display-uom.schema";
import { EditorSchema } from "~/utils/types/rich-text";

/** Org key PACKING_SLIP_DOCUMENT_CONFIG — SO Create Packing Slip PDF (SC-2489). */
export const PACKING_SLIP_DOCUMENT_CONFIG_VERSION = 1;

/** Create Packing Slip modal — optional overrides for section toggles (SC-2489). */
export const PackingSlipModalSlotsSchema = DocumentCustomTextModalSlotsSchema.extend({
  showDotHazmatPlacards: z.boolean().default(false),
  showShippersChecklist: z.boolean().default(false),
  showShippersCertification: z.boolean().default(false),
});

export type PackingSlipModalSlots = z.infer<typeof PackingSlipModalSlotsSchema>;

/** Per-create modal overrides — rich text + section toggles; not persisted on the order. */
export const PackingSlipPdfInputSchema = z.object({
  customTextHeader: EditorSchema.optional(),
  customTextFooter: EditorSchema.optional(),
  showDotHazmatPlacards: z.boolean().optional(),
  showShippersChecklist: z.boolean().optional(),
  showShippersCertification: z.boolean().optional(),
  displayUomOverride: DisplayUomOverrideSchema,
});

export type PackingSlipPdfInput = z.infer<typeof PackingSlipPdfInputSchema>;

export const emptyPackingSlipModalSlots = (): PackingSlipModalSlots => ({
  ...emptyDocumentCustomTextModalSlots(),
  showDotHazmatPlacards: false,
  showShippersChecklist: false,
  showShippersCertification: false,
});

export const emptyPackingSlipPdfInput = (): PackingSlipPdfInput => ({});

export const PackingSlipDocumentConfigSchema = z.object({
  configVersion: z.literal(PACKING_SLIP_DOCUMENT_CONFIG_VERSION).default(PACKING_SLIP_DOCUMENT_CONFIG_VERSION),
  /** Org default for DOT hazardous materials placards on PDF (matches legacy layout when true). */
  showDotHazmatPlacards: z.boolean().default(true),
  /** Org default for shipper's checklist on PDF (matches legacy layout when true). */
  showShippersChecklist: z.boolean().default(true),
  /** Org default for shipper's certification block on PDF (matches legacy layout when true). */
  showShippersCertification: z.boolean().default(true),
  boilerplate: DocumentCustomTextBoilerplateSchema.default(emptyDocumentCustomTextBoilerplate()),
  modalSlots: PackingSlipModalSlotsSchema.default(emptyPackingSlipModalSlots()),
});

export type PackingSlipDocumentConfig = z.infer<typeof PackingSlipDocumentConfigSchema>;

export const emptyPackingSlipDocumentConfig = (): PackingSlipDocumentConfig => ({
  configVersion: PACKING_SLIP_DOCUMENT_CONFIG_VERSION,
  showDotHazmatPlacards: true,
  showShippersChecklist: true,
  showShippersCertification: true,
  boilerplate: emptyDocumentCustomTextBoilerplate(),
  modalSlots: emptyPackingSlipModalSlots(),
});

export const parsePackingSlipDocumentConfig = (raw: unknown): PackingSlipDocumentConfig => PackingSlipDocumentConfigSchema.parse(raw);

/** Parse stored JSON or return null when corrupt (PDF fail-soft). */
export const tryParsePackingSlipDocumentConfigString = (raw: string): PackingSlipDocumentConfig | null => {
  try {
    const parsed: unknown = JSON.parse(raw);
    const result = PackingSlipDocumentConfigSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
};

export class PackingSlipDocumentConfigParseError extends Error {
  constructor(message = "Packing slip document settings could not be read. Contact support before saving.") {
    super(message);
    this.name = "PackingSlipDocumentConfigParseError";
  }
}

/** Settings reads — fail-hard when stored JSON is present but invalid. */
export const parsePackingSlipDocumentConfigForSettings = (raw: string | null | undefined): PackingSlipDocumentConfig => {
  if (!raw?.trim()) {
    return emptyPackingSlipDocumentConfig();
  }
  const parsed = tryParsePackingSlipDocumentConfigString(raw);
  if (!parsed) {
    throw new PackingSlipDocumentConfigParseError();
  }
  return parsed;
};

/** Super-admin org setting overwrite for PACKING_SLIP_DOCUMENT_CONFIG. */
export const assertPackingSlipDocumentOrgSettingValueForWrite = (value: string): void => {
  if (!value?.trim()) {
    return;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    throw new Error("Invalid packing slip document config JSON");
  }
  const result = PackingSlipDocumentConfigSchema.safeParse(parsed);
  if (!result.success) {
    throw new Error("Invalid packing slip document config schema");
  }
  assertDocumentCustomTextBoilerplateEditorSettings(result.data.boilerplate);
};

export const serializePackingSlipDocumentConfig = (config: PackingSlipDocumentConfig): string =>
  JSON.stringify(PackingSlipDocumentConfigSchema.parse(config));
